import React from "react";
import { inputClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import {
  BirthDateValue,
  MAX_APPROXIMATE_AGE,
  MIN_APPROXIMATE_AGE,
  computeAgeFromBirthDate,
  todayIsoDate,
} from "../../../utils/age";

type Props = {
  /** Couple date de naissance / âge approximatif en cours d'édition. */
  value: BirthDateValue;
  onChange: (value: BirthDateValue) => void;
  /** `dateOfBirthEstimated` du patient chargé : la date vient d'un âge. */
  estimated?: boolean;
  /** Habillage du libellé, aligné sur l'écran hôte. */
  labelClassName?: string;
  className?: string;
};

const DEFAULT_LABEL_CLASS = "block text-sm font-medium text-slate-600";

/**
 * Couple « Date de naissance » / « Âge approximatif » des écrans qui
 * éditent un patient.
 *
 * La date de naissance est le champ principal : dès qu'elle est saisie,
 * l'âge approximatif est vidé, désactivé, et remplacé par l'âge calculé en
 * lecture seule. Effacer la date réactive l'âge approximatif, qui reste le
 * repli quand la date est inconnue — cas courant au comptoir.
 *
 * Le serveur applique la même hiérarchie : une date envoyée l'emporte
 * toujours sur un âge. Ce composant n'a pas d'état interne, l'appelant
 * garde la source de vérité du formulaire.
 */
const BirthDateField = ({
  value,
  onChange,
  estimated = false,
  labelClassName = DEFAULT_LABEL_CLASS,
  className,
}: Props) => {
  const hasBirthDate = Boolean(value.dateOfBirth);
  const computedAge = computeAgeFromBirthDate(value.dateOfBirth);
  const isBirthDateInFuture = hasBirthDate && computedAge === null;

  const handleBirthDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextBirthDate = event.target.value;
    onChange({
      dateOfBirth: nextBirthDate,
      // Saisir une date rend l'âge approximatif caduc : on le vide pour ne
      // jamais l'envoyer à côté d'une date exacte.
      approximateAge: nextBirthDate ? "" : value.approximateAge,
    });
  };

  const handleApproximateAgeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => onChange({ ...value, approximateAge: event.target.value });

  return (
    <div className={cn("space-y-2", className)}>
      <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
        <label className="block w-full">
          <span className={labelClassName}>Date de naissance</span>
          <input
            type="date"
            name="dateOfBirth"
            value={value.dateOfBirth}
            max={todayIsoDate()}
            onChange={handleBirthDateChange}
            className={inputClass}
          />
        </label>
        <label className="block w-full">
          <span className={labelClassName}>
            {hasBirthDate ? "Âge" : "Âge approximatif"}
          </span>
          <input
            type="number"
            name="approximateAge"
            value={hasBirthDate ? computedAge ?? "" : value.approximateAge}
            min={MIN_APPROXIMATE_AGE}
            max={MAX_APPROXIMATE_AGE}
            placeholder="Ex. 45"
            disabled={hasBirthDate}
            onChange={handleApproximateAgeChange}
            className={cn(
              inputClass,
              hasBirthDate && "cursor-not-allowed text-slate-500"
            )}
          />
        </label>
      </div>

      {isBirthDateInFuture && (
        <p className="text-xs font-medium text-rose-600">
          La date de naissance ne peut pas être dans le futur.
        </p>
      )}

      {estimated && !hasBirthDate && (
        <p className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
          <i className="bx bx-info-circle text-sm" />
          Date estimée à partir de l'âge — à compléter
        </p>
      )}

      <p className="text-xs text-slate-400">
        {hasBirthDate
          ? "L'âge est calculé à partir de la date de naissance."
          : "Renseignez l'âge approximatif uniquement si la date de naissance est inconnue."}
      </p>
    </div>
  );
};

export default BirthDateField;
